import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useCustomerVehiclesSelector } from '@makemydeal/dr-activities-customer-vehicles';
import { useCustomerSelector } from '@makemydeal/dr-sales-checkin-store';
import { selectors, fetchCustomerVehicleDealsByCcid } from '@makemydeal/dr-sales-checkin-store';

/**
 * Hook to fetch customer vehicle deals when a customer is checked in.
 * Ensures the BFF call is made once per unique consumer ID.
 * Deals are stored in the customerVehicles Redux slice.
 * The slideout is opened separately when the user clicks the "Deals" button.
 *
 * @returns Object containing:
 * - isDealsBtnVisible: Whether deals button should show (toggle + deals count > 1)
 * - isOpen: Whether the customer vehicles slideout is open
 */
export const useFetchCustomerVehicleDeals = () => {
    const dispatch = useDispatch();
    const { isOpen, dealsCount } = useCustomerVehiclesSelector();
    const { checkedInCustomer } = useCustomerSelector();
    const fetchedConsumerIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        // Only fetch if the toggle is enabled

        const consumerId = checkedInCustomer?.consumerId;

        if (!consumerId || fetchedConsumerIds.current.has(consumerId)) {
            return;
        }

        fetchedConsumerIds.current.add(consumerId);

        dispatch(fetchCustomerVehicleDealsByCcid(selectors, consumerId) as any);
    }, [checkedInCustomer?.consumerId, dispatch]);

    // Compute button visibility: toggle ON + more than one deal
    const isDealsBtnVisible = (dealsCount ?? 0) > 1;

    return {
        isDealsBtnVisible,
        isOpen
    };
};
